import { useEffect, useState } from "react";
import { FaXmark, FaDownload, FaCopy, FaLink } from "react-icons/fa6";
import { getFileUrl } from "../../lib/telegram";
import { downloadFile } from "../../lib/fileFetch";
import { loadPreviewBlob, loadPreviewText } from "../../lib/previewLoad";
import { buildFileShareUrl } from "../../lib/everythingShare";
import { ev } from "../../lib/everythingCopy";
import { categorize, formatBytes } from "./FileTile";
import { getLanguage, highlight } from "../../lib/syntaxHighlight";

export function Preview({ file, onClose }) {
  const [url, setUrl] = useState(null);
  const [textBody, setTextBody] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareError, setShareError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let revokeUrl = null;

    async function load() {
      setLoading(true);
      setError(null);
      setUrl(null);
      setTextBody(file.content ?? null);
      setCopied(false);
      setCopyError(false);
      setLinkCopied(false);
      setShareError(false);

      const kind = categorize(file);
      if (kind === "archive" || kind === "binary") {
        setLoading(false);
        return;
      }

      try {
        const { blob, blobUrl, source } = await loadPreviewBlob(file);
        if (cancelled) return;

        if (source === "cache" || source === "telegram") {
          revokeUrl = blobUrl;
        }

        const text = await loadPreviewText(file, blob);
        if (cancelled) return;

        if (text != null) setTextBody(text);
        setUrl(blobUrl);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [file]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const kind = categorize(file);
  const isTextPreview = kind === "code" || kind === "note" || textBody !== null;
  const canCopy = isTextPreview && textBody !== null && !loading && !error;

  async function handleCopy() {
    if (!textBody) return;
    try {
      await navigator.clipboard.writeText(textBody);
      setCopyError(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      setCopyError(true);
      setTimeout(() => setCopyError(false), 2000);
    }
  }

  async function handleShare() {
    try {
      await navigator.clipboard.writeText(buildFileShareUrl(file.id));
      setShareError(false);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      setLinkCopied(false);
      setShareError(true);
      setTimeout(() => setShareError(false), 2000);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/80 backdrop-blur-sm flex flex-col"
      onClick={onClose}
    >
      <div
        className="flex items-center justify-between gap-3 px-4 md:px-8 py-3 text-white shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0">
          <p className="font-medium text-sm md:text-base truncate">{file.name}</p>
          <p className="text-xs text-white/60">
            {formatBytes(file.size)}{file.mime && ` · ${file.mime}`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
          >
            <FaLink className="w-3.5 h-3.5" />
            {shareError ? ev.shareFailed : linkCopied ? ev.linkCopied : ev.share}
          </button>
          {canCopy && (
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
            >
              <FaCopy className="w-3.5 h-3.5" />
              {copyError ? ev.copyFailed : copied ? ev.copied : ev.copy}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              downloadFile(file, () => (url && url.startsWith("blob:") ? url : getFileUrl(file))).catch(() => {
                if (url) window.open(url, "_blank");
              });
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm font-medium transition-colors"
          >
            <FaDownload className="w-3.5 h-3.5" />
            {ev.download}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label={ev.close}
          >
            <FaXmark className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center p-4 md:p-8">
        {error && (
          <p className="text-zinc-400 text-sm text-center max-w-md">{error}</p>
        )}
        {!error && loading && <Spinner />}
        {!error && !loading && url && kind === "image" && (
          <img
            src={url}
            alt={file.name}
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {!error && !loading && url && kind === "video" && (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {!error && !loading && url && kind === "audio" && (
          <audio
            src={url}
            controls
            autoPlay
            className="w-full max-w-xl"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {!error && !loading && url && kind === "pdf" && (
          <embed
            src={`${url}#toolbar=0&navpanes=0`}
            type="application/pdf"
            title={file.name}
            className="w-full h-full bg-white rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {!error && !loading && isTextPreview && textBody !== null && (
          <TextPreview file={file} text={textBody} />
        )}
        {!error && !loading && !isTextPreview && (kind === "binary" || kind === "archive") && (
          <div
            className="text-center text-white/80"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm">{ev.previewUnavailable}</p>
            <p className="text-xs text-white/50 mt-1">{ev.previewUseDownload}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <span className="inline-block w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
  );
}

function TextPreview({ file, text }) {
  const language = getLanguage(file);
  const highlighted = language ? highlight(text, language) : null;

  const wrapClass =
    "w-full max-w-5xl max-h-full overflow-auto text-xs md:text-sm rounded-lg shadow-2xl";
  const codeClass =
    "block p-5 font-mono whitespace-pre-wrap break-words";

  if (highlighted) {
    return (
      <pre className={wrapClass} onClick={(e) => e.stopPropagation()}>
        <code
          className={`hljs language-${language} ${codeClass}`}
          dangerouslySetInnerHTML={{ __html: highlighted }}
        />
      </pre>
    );
  }

  return (
    <pre
      className={`${wrapClass} bg-zinc-950`}
      onClick={(e) => e.stopPropagation()}
    >
      <code className={`${codeClass} text-zinc-100`}>{text}</code>
    </pre>
  );
}
